import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Eye, Mail, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Loading, PageHeader, Pill, Section, Spinner } from "@/components/common";

const FREQS = [
  { value: "daily", label: "Daily briefing" },
  { value: "weekly", label: "Weekly coaching digest" },
  { value: "monthly", label: "Monthly performance review" },
  { value: "quarterly", label: "Quarterly business review" },
  { value: "yearly", label: "Annual summary" },
];

export function EmailTags({ value = [], onChange, testId, placeholder = "Add email and press Enter" }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (v && /.+@.+\..+/.test(v) && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-[#1f293d] bg-[#0f172a] p-1.5 min-h-[2.5rem]" data-testid={testId}>
      {value.map((e) => (
        <span key={e} className="inline-flex items-center gap-1 rounded bg-blue-500/15 border border-blue-500/30 text-blue-200 text-xs font-mono px-1.5 py-0.5">
          {e}
          <button data-testid={`${testId}-remove-${e}`} onClick={() => onChange(value.filter((x) => x !== e))} className="hover:text-white">×</button>
        </span>
      ))}
      <input data-testid={`${testId}-input`} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add} placeholder={value.length ? "" : placeholder} className="flex-1 min-w-[160px] bg-transparent text-xs text-slate-100 outline-none px-1 h-6" />
    </div>
  );
}

function ScheduleForm({ onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "Leadership performance report", frequency: "weekly", recipients: [], hour: 8, enabled: true, include_insights: true });
  const m = useMutation({
    mutationFn: () => api.post("/reports/schedules", form).then((r) => r.data),
    onSuccess: () => { toast.success("Schedule created"); qc.invalidateQueries({ queryKey: ["schedules"] }); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="space-y-4" data-testid="schedule-form">
      <div><label className="eyebrow block mb-1">Report name</label><Input data-testid="schedule-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0f172a] border-[#1f293d]" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="eyebrow block mb-1">Frequency</label>
          <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
            <SelectTrigger data-testid="schedule-frequency-select" className="bg-[#0f172a] border-[#1f293d]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#2d3d58]">{FREQS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="eyebrow block mb-1">Send at hour (local)</label><Input data-testid="schedule-hour-input" type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })} className="bg-[#0f172a] border-[#1f293d] font-mono" /></div>
      </div>
      <div><label className="eyebrow block mb-1">Recipients</label><EmailTags testId="schedule-recipients" value={form.recipients} onChange={(v) => setForm({ ...form, recipients: v })} /></div>
      <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="schedule-insights-switch" checked={form.include_insights} onCheckedChange={(v) => setForm({ ...form, include_insights: v })} /> Include GPT-5.4 executive insights & coaching themes</label>
      <div className="flex justify-end gap-2"><Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button><Button data-testid="schedule-submit-button" size="sm" className="bg-blue-600 hover:bg-blue-500" disabled={m.isPending || !form.recipients.length || !form.name} onClick={() => m.mutate()}>{m.isPending ? <Spinner className="text-white" /> : "Create schedule"}</Button></div>
    </div>
  );
}

function ScheduleCard({ s }) {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["schedules"] }); qc.invalidateQueries({ queryKey: ["report-runs"] }); };
  const patch = useMutation({ mutationFn: (body) => api.patch(`/reports/schedules/${s.id}`, body).then((r) => r.data), onSuccess: refresh, onError: (e) => toast.error(errMsg(e)) });
  const del = useMutation({ mutationFn: () => api.delete(`/reports/schedules/${s.id}`), onSuccess: () => { toast.success("Schedule deleted"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const send = useMutation({ mutationFn: () => api.post(`/reports/schedules/${s.id}/send-now`, {}).then((r) => r.data), onSuccess: (d) => { d.status === "sent" ? toast.success(`Report emailed to ${d.recipients.join(", ")}`) : toast.error(`Email failed: ${d.error}`); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div className="panel panel-hover p-4 fade-up" data-testid={`schedule-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Pill tone="cyan">{s.frequency}</Pill><h3 className="text-base font-bold text-slate-100 truncate">{s.name}</h3></div>
          <div className="text-xs text-slate-500 mt-1 font-mono flex flex-wrap gap-x-3">
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> next {s.next_run_at ? fmtDateTime(s.next_run_at) : "—"} · {String(s.hour).padStart(2, "0")}:00 local</span>
            {s.last_run_at && <span>last {relTime(s.last_run_at)} <Pill tone={s.last_status === "sent" ? "emerald" : "red"}>{s.last_status}</Pill></span>}
          </div>
        </div>
        <Switch data-testid={`schedule-toggle-${s.id}`} checked={s.enabled} onCheckedChange={(v) => patch.mutate({ enabled: v })} />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">{s.recipients.map((r) => <span key={r} className="text-xs font-mono text-slate-300 bg-[#0f172a] border border-[#1f293d] rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Mail className="h-3 w-3 text-slate-500" />{r}</span>)}</div>
      <div className="mt-3 flex gap-2">
        <Button data-testid={`schedule-send-now-${s.id}`} size="sm" variant="outline" className="h-8 text-xs border-[#2d3d58] bg-[#0f172a] hover:bg-[#161f30]" disabled={send.isPending} onClick={() => send.mutate()}>{send.isPending ? <Spinner /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Send now</Button>
        <Button data-testid={`schedule-delete-${s.id}`} size="sm" variant="ghost" className="h-8 text-xs text-slate-400 hover:text-red-300" disabled={del.isPending} onClick={() => del.mutate()}><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete</Button>
      </div>
    </div>
  );
}

export default function Reports() {
  const [open, setOpen] = useState(false);
  const [previewFreq, setPreviewFreq] = useState("weekly");
  const [runHtml, setRunHtml] = useState(null);
  const schedules = useQuery({ queryKey: ["schedules"], queryFn: () => api.get("/reports/schedules").then((r) => r.data) });
  const runs = useQuery({ queryKey: ["report-runs"], queryFn: () => api.get("/reports/runs").then((r) => r.data) });
  const preview = useQuery({ queryKey: ["report-preview", previewFreq], queryFn: () => api.get("/reports/preview", { params: { frequency: previewFreq } }).then((r) => r.data) });
  const openRun = async (id) => { const { data } = await api.get(`/reports/runs/${id}/html`); setRunHtml(data.html); };

  return (
    <>
      <PageHeader eyebrow="Automated reporting" title="Scheduled reports" description="Daily, weekly, monthly, quarterly or yearly performance reports — call volume, coaching scores by agent, text-response SLA, anomalies and GPT-5.4 executive insights — emailed to the stakeholders you choose." actions={<Button data-testid="new-schedule-button" size="sm" className="h-8 bg-blue-600 hover:bg-blue-500 text-xs" onClick={() => setOpen(true)}><Plus className="h-3.5 w-3.5 mr-1.5" /> New schedule</Button>} testId="reports-header" />

      <div className="grid xl:grid-cols-[1fr_1.1fr] gap-4 items-start">
        <div className="space-y-4">
          <div className="space-y-3" data-testid="schedules-list">
            {schedules.isLoading ? <Loading /> : schedules.data?.length ? schedules.data.map((s) => <ScheduleCard key={s.id} s={s} />) : (
              <Section><EmptyState icon={CalendarClock} title="No schedules yet" hint="Create a daily briefing for managers or a weekly coaching digest for leadership." action={<Button data-testid="empty-new-schedule-button" size="sm" className="bg-blue-600 hover:bg-blue-500" onClick={() => setOpen(true)}>Create first schedule</Button>} testId="schedules-empty" /></Section>
            )}
          </div>
          <Section title="Delivery history" eyebrow="Report runs" padded={false} testId="report-runs">
            {runs.data?.length ? (
              <ul className="divide-y divide-[#1f293d]">
                {runs.data.map((r) => (
                  <li key={r.id} className="px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs row-hover cursor-pointer" onClick={() => openRun(r.id)} data-testid={`report-run-${r.id}`}>
                    <Pill tone={r.status === "sent" ? "emerald" : "red"}>{r.status}</Pill>
                    <Pill tone="cyan">{r.frequency}</Pill>
                    <span className="text-slate-200">{r.name}</span>
                    <span className="text-slate-500 font-mono">{r.recipients?.length} recipient{r.recipients?.length === 1 ? "" : "s"} · {r.summary?.calls ?? 0} calls · avg {r.summary?.avg_score ?? "—"}</span>
                    <span className="ml-auto text-slate-500 font-mono">{fmtDateTime(r.sent_at)} · {r.trigger}</span>
                    <Eye className="h-3.5 w-3.5 text-slate-500" />
                  </li>
                ))}
              </ul>
            ) : <EmptyState icon={Mail} title="No reports sent yet" />}
          </Section>
        </div>

        <Section
          title="Live preview"
          eyebrow="What recipients will receive"
          testId="report-preview"
          actions={
            <Select value={previewFreq} onValueChange={setPreviewFreq}>
              <SelectTrigger data-testid="preview-frequency-select" className="h-8 text-xs bg-[#0f172a] border-[#1f293d] w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#2d3d58]">{FREQS.map((f) => <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>)}</SelectContent>
            </Select>
          }
          padded={false}
        >
          {preview.isLoading ? <Loading label="Building report" /> : <iframe title="Report preview" srcDoc={preview.data?.html} className="w-full h-[760px] bg-white rounded-b-lg" data-testid="report-preview-frame" />}
        </Section>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111827] border-[#2d3d58]"><DialogHeader><DialogTitle className="font-display">New report schedule</DialogTitle></DialogHeader><ScheduleForm onClose={() => setOpen(false)} /></DialogContent>
      </Dialog>
      <Dialog open={!!runHtml} onOpenChange={() => setRunHtml(null)}>
        <DialogContent className="bg-[#111827] border-[#2d3d58] max-w-3xl"><DialogHeader><DialogTitle className="font-display">Sent report</DialogTitle></DialogHeader><iframe title="Sent report" srcDoc={runHtml || ""} className="w-full h-[70vh] bg-white rounded" /></DialogContent>
      </Dialog>
    </>
  );
}
